import type { Block, BlockMilestone } from '@basehub/data/replicache/index'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useCommit } from '~/hooks-data/use-commit'
import * as React from 'react'
import Link from 'next/link'
import { useTabsStore } from '../tabs/store'
import {
  CommitTime,
  PrimitiveCommit,
} from '../../../history/_components/sidebar/primitive-commit'
import { flattenContributors } from '@basehub/data/replicache/schema/block-milestone/util'
import { ContributorName } from '~/components/contributors-tooltip'
import {
  CommitIcon,
  Pencil1Icon,
  PlusIcon,
  RowSpacingIcon,
} from '@radix-ui/react-icons'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { clsx } from 'clsx'
import { cva } from 'class-variance-authority'
import { Filters } from './filters'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import { useUserStore } from '~/app/_store/user-store'
import { DateRange } from 'react-day-picker'
import { useCustomSyncerSub } from '~/replicache/frontend/use-custom-syncer-sub'

const DEBUG_MILESTONES = false

const $paginatorStyles = cva(
  clsx(
    'cursor-default w-full px-1 py-1.5 flex items-center gap-x-1 text-dark-faint text-xs font-medium border-y border-dark-border-solid mb-6',
    'relative after:content-[""] after:absolute after:top-full after:h-[1.5625rem] after:w-px after:bg-dark-border-solid after:left-3'
  ),
  {
    variants: {
      hover: {
        true: 'hover:text-dark-muted transition-colors',
      },
    },
  }
)

const $commitContainer = cva(
  'group/wrapper grid grid-cols-[auto_1fr] grid-rows-[auto_1fr] gap-x-1.5 place-items-center'
)

export const BlockHistory = ({ block }: { block: Block }) => {
  const { replicache } = useRepoReplicache().data ?? {}
  const userId = useUserStore((s) => s.optimisticUser?.id)
  const range = useUserPreferences((s) => s.historyPanelFilters?.range)
  const membersSelected = useUserPreferences(
    (s) => s.historyPanelFilters?.members ?? []
  )
  const limit = 5
  const [getAll, setGetAll] = React.useState(false)

  const handleMembersChange = React.useCallback(
    (valueOrFunction: ((prev: string[]) => string[]) | string[]) => {
      if (!userId || !replicache) return
      let result
      if (typeof valueOrFunction === 'function') {
        result = valueOrFunction(membersSelected)
      } else {
        result = valueOrFunction
      }

      replicache.mutate.setCommitsFilters({
        filterType: 'historyPanelFilters',
        userId,
        members: result,
      })
    },
    [userId, replicache, membersSelected]
  )

  const handleRangeChange = React.useCallback(
    (value: DateRange | undefined) => {
      if (!userId || !replicache) return
      let range
      if (!value?.to || !value.from) {
        range = null
      } else {
        range = {
          from: value.from.toISOString(),
          to: value.to.toISOString(),
        }
      }

      replicache.mutate.setCommitsFilters({
        filterType: 'historyPanelFilters',
        userId,
        range,
      })
    },
    [userId, replicache]
  )

  const rangeSelection = React.useMemo(() => {
    if (!range) return undefined
    return { from: new Date(range.from), to: new Date(range.to) }
  }, [range])

  const { data: blockMilestonesData } = useCustomSyncerSub(replicache, {
    syncer: 'blockMilestones',
    params: {
      sourceBlockId: idToSourceBlockId(block),
      members: membersSelected.length ? membersSelected : undefined,
      dateRange: range ?? undefined,
      limit,
      getAll,
    },
  })

  const lastMilestone =
    blockMilestonesData?.result[blockMilestonesData.result.length - 1]

  const isEmpty =
    blockMilestonesData?.hasMore === false &&
    blockMilestonesData?.result.length === 0

  return (
    <div>
      <Filters
        membersProps={{
          selection: membersSelected,
          handleSelection: handleMembersChange,
        }}
        rangeProps={{
          handleSelection: handleRangeChange,
          selection: rangeSelection,
        }}
      />
      {isEmpty && (
        <div className="flex flex-col text-center pt-1 gap-y-2 items-center pb-3">
          <div className="relative border border-dark-border-solid bg-dark-shade rounded-full size-8 text-dark-dim">
            <CommitIcon className="absolute -translate-x-1/2 -translate-y-1/2 shrink-0 top-1/2 left-1/2" />
          </div>
          <span className="text-dark-muted text-xs leading-normal text-pretty">
            {Boolean(membersSelected.length || range)
              ? 'No matching results.'
              : 'All block changes will be displayed here.'}
          </span>
        </div>
      )}
      {blockMilestonesData?.result.map((milestone, index) => {
        if (!milestone.commitId) {
          // is WIP milestone
          return (
            <React.Fragment key={milestone.id}>
              <div className={$commitContainer()}>
                <div className="relative bg-dark-control-base rounded-3xl size-6 text-white">
                  <Pencil1Icon
                    height={15}
                    width={15}
                    className="absolute -translate-x-1/2 -translate-y-1/2 shrink-0 top-1/2 left-1/2"
                  />
                </div>
                <div className="w-px h-full bg-dark-border-solid group-last/wrapper:hidden" />
                <PrimitiveCommit
                  className="col-start-2 mb-5 row-span-full"
                  message={
                    <>
                      WIP
                      {DEBUG_MILESTONES && (
                        <SimpleTooltip
                          content={`Deps: ${JSON.stringify(
                            milestone.dependencies,
                            null,
                            2
                          )}`}
                        >
                          <span className="text-dark-muted text-[11px] truncate">
                            {milestone.currentStatus} : {milestone.id}
                          </span>
                        </SimpleTooltip>
                      )}
                    </>
                  }
                  contributors={flattenContributors(milestone.contributors)}
                  date={milestone.updatedAt ?? milestone.createdAt}
                  id={null}
                  key={`${milestone.id}:${milestone.updatedAt}`}
                  reordered={Boolean(milestone.reordered)}
                />
              </div>
            </React.Fragment>
          )
        }
        return (
          <React.Fragment key={milestone.id}>
            {blockMilestonesData?.hasMore && index === limit && (
              <button
                className={$paginatorStyles({ hover: true })}
                onClick={() => setGetAll(true)}
              >
                <RowSpacingIcon height={15} width={15} className="shrink-0" />
                <span>
                  See {blockMilestonesData.remaining} commit
                  {blockMilestonesData.remaining > 1 && 's'} in between
                </span>
              </button>
            )}
            <MilestoneCommittedItem key={milestone.id} milestone={milestone} />
          </React.Fragment>
        )
      })}
      {lastMilestone?.currentStatus === 'created' && (
        <div className="font-medium text-[0.75rem] leading-normal text-dark-faint flex gap-x-1.5 items-start">
          <span className="relative border border-dark-border-solid bg-dark-shade rounded-3xl size-6 text-dark-dim shrink-0">
            <PlusIcon
              height={15}
              width={15}
              className="absolute -translate-x-1/2 -translate-y-1/2 shrink-0 top-1/2 left-1/2"
            />
          </span>
          <span className="mt-[0.188rem] text-pretty">
            Block created{' '}
            <span className="text-dark-muted">
              <CommitTime date={lastMilestone.createdAt} />
            </span>{' '}
            by{' '}
            <span className="text-dark-muted">
              <ContributorName
                short
                id={lastMilestone.contributors.created?.[0] ?? ''}
              />
            </span>
          </span>
        </div>
      )}
    </div>
  )
}

const MilestoneCommittedItem = ({
  milestone,
}: {
  milestone: BlockMilestone
}) => {
  const commitQuery = useCommit(milestone.commitId)
  const { repoPage, teamSlug, repoSlug, branchOrCommit } = useDashboardSlugs()
  const addTab = useTabsStore((s) => s.addTab)

  const commitKey = React.useMemo(() => {
    const commitSubPath = milestone.parentId
      ? `${milestone.parentId}:${milestone.sourceBlockId}`
      : milestone.sourceBlockId

    return `commit:${milestone.commitId}:${commitSubPath}`
  }, [milestone.parentId, milestone.sourceBlockId, milestone.commitId])

  const handleClick = React.useCallback(
    (e: React.MouseEvent) => {
      if (e.metaKey) return
      e.preventDefault()
      addTab({
        key: commitKey,
        partiallyOpen: true,
      })
    },
    [addTab, commitKey]
  )

  const href = React.useMemo(
    () => `/${teamSlug}/${repoSlug}/${repoPage}/${branchOrCommit}/${commitKey}`,
    [teamSlug, repoSlug, repoPage, branchOrCommit, commitKey]
  )

  return (
    <Link
      className={$commitContainer({
        className:
          'rounded outline-1 outline-offset-4 outline-dark-control-base focus-visible:outline cursor-default',
      })}
      href={href}
      onClick={handleClick}
    >
      <div className="relative border border-dark-border-solid bg-dark-shade rounded-3xl size-6 text-dark-dim">
        <CommitIcon
          height={15}
          width={15}
          className="absolute -translate-x-1/2 -translate-y-1/2 shrink-0 top-1/2 left-1/2"
        />
      </div>
      <div className="w-px h-full bg-dark-border-solid group-last/wrapper:hidden" />
      <PrimitiveCommit
        className="col-start-2 mb-6 row-span-full"
        message={
          <>
            {commitQuery.data?.result?.message ?? ''}
            <br />
            {DEBUG_MILESTONES && (
              <SimpleTooltip
                content={`Deps: ${JSON.stringify(
                  milestone.dependencies,
                  null,
                  2
                )}`}
              >
                <span className="text-dark-muted text-[11px] truncate">
                  {milestone.currentStatus} : {milestone.id}
                </span>
              </SimpleTooltip>
            )}
          </>
        }
        contributors={flattenContributors(milestone.contributors)}
        date={milestone.updatedAt ?? milestone.createdAt}
        id={milestone.commitId ?? null}
        reordered={Boolean(milestone.reordered)}
      />
    </Link>
  )
}
